import { useState, useEffect } from "react";

interface MembershipInfo {
  status: 'active' | 'inactive';
  plan?: string;
  expires_at?: string;
  days_remaining?: number;
  message?: string;
}

interface MemberInfo {
  id: number;
  name: string;
  phone?: string;
  email?: string;
}

interface MemberCheckResponse {
  exists: boolean;
  member?: MemberInfo;
  membership?: MembershipInfo;
  message?: string;
}

interface MemberCheckProps {
  user: {
    email?: string;
    phone?: string;
    name?: string;
  };
  onResult?: (result: MemberCheckResponse) => void;
  autoCheck?: boolean; // 자동 체크 여부
  onProceedAsNewMember?: () => void; // 신규회원으로 진행
  onBack?: () => void; // 뒤로가기
}

export default function MemberCheck({ user, onResult, autoCheck = true, onProceedAsNewMember, onBack }: MemberCheckProps) {
  const [identifier, setIdentifier] = useState(user.email || user.phone || "");
  const [status, setStatus] = useState<"idle" | "loading" | "found" | "not_found" | "error">("idle");
  const [result, setResult] = useState<MemberCheckResponse | null>(null);
  const [hasAutoChecked, setHasAutoChecked] = useState(false);
  const [initialAutoCheckDone, setInitialAutoCheckDone] = useState(false);

  // 화면 진입 시 자동 체크 (최초 1회만)
  useEffect(() => {
    if (autoCheck && (user.email || user.phone) && !initialAutoCheckDone) {
      handleCheck();
      setHasAutoChecked(true);
      setInitialAutoCheckDone(true);
    }
  }, [autoCheck, user.email, user.phone, initialAutoCheckDone]);

  const handleCheck = async () => {
    setStatus("loading");

    try {
      let requestBody;

      // 최초 자동 체크인 경우에만 user 정보 사용, 나머지는 모두 identifier 사용
      if (hasAutoChecked && !initialAutoCheckDone) {
        // 최초 자동 체크 - user 정보 사용
        requestBody = {
          email: user.email || null,
          phone: user.phone || null,
          name: user.name || null
        };
      } else {
        // 수동 체크 또는 재체크 - identifier와 user.name 사용
        if (!identifier.trim()) {
          setStatus("idle");
          return;
        }

        // identifier가 이메일인지 전화번호인지 구분
        const isEmail = identifier.includes('@');
        requestBody = {
          email: isEmail ? identifier.trim() : null,
          phone: isEmail ? null : identifier.trim(),
          name: user.name || null // 로그인한 사용자 이름 사용
        };
      }

      const res = await fetch('/api/v1/members/check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
        },
        body: JSON.stringify(requestBody)
      });

      const data: MemberCheckResponse = await res.json();
      //console.log('data', data);

      setResult(data);

      // 최초 자동체크인 경우에만 바로 콜백 호출
      if (hasAutoChecked && !initialAutoCheckDone) {
        onResult?.(data);
      }

      if (data.exists) {
        setStatus("found");
      } else {
        setStatus("not_found");
      }

    } catch (err) {
      console.error('[MemberCheck] Error:', err);
      setStatus("error");
      setResult(null);
    }
  };

  const getMembershipStatusText = (membership?: MembershipInfo) => {
    if (!membership) return null;

    if (membership.status === 'active') {
      return (
        <div className="space-y-1">
          <div className="text-sm font-medium text-green-600">
            활성 멤버십: {membership.plan || '기본'}
          </div>
          {membership.days_remaining && (
            <div className="text-xs text-gray-500">
              {membership.days_remaining}일 남음
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="text-sm text-orange-600">
          {membership.message || '활성 멤버십이 없습니다'}
        </div>
      );
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-2">
            {onBack && (
              <div className="flex items-center justify-between mb-4">
                {/* 자동체크 모드일 때는 회원구분 화면으로, 수동입력 모드일 때는 회원확인 화면으로 */}
                {/* {hasAutoChecked && !initialAutoCheckDone ? ( */}
                  <button onClick={onBack} className="btn btn-ghost btn-sm">
                    ← 회원구분가입 화면으로 돌아가기
                  </button>
                {/* ) : (
                  <button
                    onClick={() => {
                      setStatus("idle");
                      setIdentifier(user.email || user.phone || "");
                      setResult(null);
                      setHasAutoChecked(true);

                      // 자동 검색 클릭 효과
                      handleCheck();
                    }}
                    className="btn btn-ghost btn-sm"
                  >
                    ← 돌아가기
                  </button>
                )} */}
              </div>
            )}
            <div className="flex-1"></div>
          </div>
          <h2 className="text-xl font-bold text-center text-gray-800">회원 가입 확인</h2>
          <p className="text-sm text-center text-gray-500 mt-2">
            가입 이력 확인을 위해 이메일 또는 휴대폰 번호를 확인합니다.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <input
            type="text"
            placeholder="이메일 또는 휴대폰 번호"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              (status === "loading" || (autoCheck && !!(user.email || user.phone) && hasAutoChecked))
                ? "bg-gray-100 text-gray-600"
                : "bg-white"
            }`}
            disabled={status === "loading" || (autoCheck && !!(user.email || user.phone) && hasAutoChecked)}
            readOnly={autoCheck && !!(user.email || user.phone) && hasAutoChecked}
          />

          <button
            onClick={handleCheck}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
              status === "loading" || !identifier.trim()
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
            }`}
            disabled={status === "loading" || !identifier.trim()}
          >
            {status === "loading" ? "확인 중..." : "가입여부 확인"}
          </button>

          {status === "found" && result?.exists && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="space-y-3">
                <div className="font-medium text-green-800">
                  가입된 회원입니다: {result.member?.name}
                </div>
                {getMembershipStatusText(result.membership)}
                <div className="text-sm text-green-700">
                  {result.membership?.status === 'active'
                    ? '멤버십 연장을 진행해 주세요.'
                    : '새로운 멤버십 결제를 진행해 주세요.'}
                </div>

                {/* 수동 체크인 경우에만 확인 버튼 표시 */}
                {(!hasAutoChecked || initialAutoCheckDone) && (
                  <div className="pt-2">
                    <button
                      onClick={() => onResult?.(result)}
                      className="px-4 py-2 text-base bg-green-600 text-white rounded hover:bg-green-700 transition-colors cursor-pointer font-medium"
                    >
                      이 회원으로 진행
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {status === "not_found" && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="space-y-3">
                <div className="font-medium text-yellow-800">멤버십 가입 이력이 없습니다.</div>
                <div className="text-sm text-yellow-700">
                  다른 이메일이나 전화번호로 다시 확인하시거나, 신규 회원으로 진행해 주세요.
                </div>
                <div className="flex gap-2 flex-wrap">                  
                  {onProceedAsNewMember && (
                    <button
                      onClick={onProceedAsNewMember}
                      className="px-4 py-2 text-base bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer font-medium"
                    >
                      신규회원으로 진행
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setStatus("idle");
                      setIdentifier("");
                      setResult(null);
                      setHasAutoChecked(false);
                    }}
                    className="px-4 py-2 text-base bg-yellow-300 text-yellow-800 rounded hover:bg-yellow-200 transition-colors cursor-pointer font-medium"
                  >
                    다른 정보로 확인
                  </button>
                </div>
                <div className="text-sm text-gray-600 pt-2 border-t border-yellow-200">
                  ※ 분명히 가입하신 것 같은데, 해당 방법으로 찾을 수 없으면{" "}
                  <a
                    href="/ask"
                    className="text-blue-600 hover:text-blue-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    1:1 문의게시판
                  </a>
                  으로 문의를 올려주세요.
                </div>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <span className="text-red-800">확인 중 오류가 발생했습니다. 다시 시도해 주세요.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}